import { useState, type FormEvent } from 'react';
import type {
  CareerAspirationDetail,
  CareerAspirationStatus,
  CareerAspirationsResponse,
  EmployeeProfileResponse,
  RoleListResponse,
} from '../../api/types';
import { apiPatch, apiPost, describeError, getDevEmployeeId } from '../../api/client';
import { useApiResource, type ResourceState } from '../../hooks/useApiResource';
import { FactSection } from './FactSection';
import formStyles from './AssessmentForm.module.css';
import styles from './CaptureSection.module.css';

/**
 * Career aspiration — basic capture (docs/DOMAIN_DECISIONS.md, "Development
 * and Career Aspiration Capture Scope"; `UX_ARCHITECTURE.md` §3.8).
 *
 * A flat list of `CareerAspiration` records — "employee-declared interest
 * in a role/direction" (`DOMAIN_MODEL.md` §2.19), and nothing beyond that:
 * no path visualisation, no readiness or potential linkage, none of
 * `UX_ARCHITECTURE.md` §3.8's fully-POST-MVP scope. This section exists
 * only because DC-1 requires genuine capture; it earns no nav destination
 * of its own, per DC-4.
 *
 * Same read/write split as Development immediately above: any viewer who
 * can read this profile can read these records; only the employee whose
 * aspiration it is may create or edit one.
 */
export function CareerAspirationsSection({
  profile,
  roles,
}: {
  profile: EmployeeProfileResponse;
  roles: ResourceState<RoleListResponse>;
}) {
  const isSelf = getDevEmployeeId() === profile.id;
  const aspirations = useApiResource<CareerAspirationsResponse>(`/employees/${profile.id}/career-aspirations`);
  const [creating, setCreating] = useState(false);

  function roleName(id: string | null): string | null {
    if (id === null || roles.status !== 'loaded') return null;
    const role = roles.data.roles.find((candidate) => candidate.id === id);
    return role ? (role.status === 'retired' ? `${role.name} (retired)` : role.name) : null;
  }

  return (
    <>
      <FactSection<CareerAspirationsResponse>
        id="career-aspirations"
        title="Career aspirations"
        state={aspirations}
        loadingLabel="Loading career aspirations…"
        errorTitle="Career aspirations could not be loaded"
        isEmpty={(data) => data.aspirations.length === 0}
        empty={{
          title: 'No career aspirations recorded',
          detail: isSelf
            ? 'You have not recorded a career aspiration yet. Use "Add a career aspiration" below to record one.'
            : 'Nothing has been recorded here yet. That is an absence of records, not a finding about this person.',
        }}
        summary={(data) =>
          `${data.aspirations.length} ${data.aspirations.length === 1 ? 'aspiration' : 'aspirations'} recorded`
        }
        note="A stated direction or target role, in the person's own words — nothing here is derived, scored or compared against anyone else's. Only the person an aspiration belongs to may add to it or edit it."
      >
        {(data) => (
          <ul className={styles.list}>
            {data.aspirations.map((aspiration) => (
              <AspirationCard
                key={aspiration.id}
                aspiration={aspiration}
                employeeId={profile.id}
                isSelf={isSelf}
                roleName={roleName}
                roles={roles}
                onChanged={() => aspirations.reload()}
              />
            ))}
          </ul>
        )}
      </FactSection>

      {isSelf && aspirations.status === 'loaded' ? (
        creating ? (
          <NewAspirationForm
            employeeId={profile.id}
            roles={roles}
            onCreated={() => {
              setCreating(false);
              aspirations.reload();
            }}
            onClose={() => setCreating(false)}
          />
        ) : (
          <button type="button" className={styles.addToggle} onClick={() => setCreating(true)}>
            Add a career aspiration
          </button>
        )
      ) : null}
    </>
  );
}

const STATUS_LABEL: Record<CareerAspirationStatus, string> = {
  active: 'Active',
  achieved: 'Achieved',
  abandoned: 'Abandoned',
};

function AspirationCard({
  aspiration,
  employeeId,
  isSelf,
  roleName,
  roles,
  onChanged,
}: {
  aspiration: CareerAspirationDetail;
  employeeId: string;
  isSelf: boolean;
  roleName: (id: string | null) => string | null;
  roles: ResourceState<RoleListResponse>;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const targetRoleName = roleName(aspiration.targetRoleId);

  return (
    <li className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.name}>
          {aspiration.targetRoleId
            ? (targetRoleName ?? 'A role')
            : aspiration.directionDescription || <span className={styles.absent}>No direction stated</span>}
        </span>
        <span className={styles.status}>{STATUS_LABEL[aspiration.status]}</span>
        {aspiration.priorityRank !== null ? (
          <span className={styles.status}>Priority {aspiration.priorityRank}</span>
        ) : null}
        {isSelf ? (
          <button type="button" className={styles.editToggle} onClick={() => setEditing((open) => !open)}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
        ) : null}
      </div>

      {aspiration.targetRoleId && aspiration.directionDescription ? (
        <p className={styles.notes}>{aspiration.directionDescription}</p>
      ) : null}
      {aspiration.notes ? <p className={styles.notes}>{aspiration.notes}</p> : null}

      {editing ? (
        <EditAspirationForm
          employeeId={employeeId}
          aspiration={aspiration}
          roles={roles}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </li>
  );
}

function RoleSelect({
  id,
  value,
  onChange,
  roles,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  roles: ResourceState<RoleListResponse>;
}) {
  return (
    <select id={id} className={styles.select} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">No target role</option>
      {roles.status === 'loaded'
        ? roles.data.roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.status === 'retired' ? `${role.name} (retired)` : role.name}
            </option>
          ))
        : null}
    </select>
  );
}

type SubmitState = { status: 'editing' } | { status: 'submitting' } | { status: 'failed'; message: string };

function NewAspirationForm({
  employeeId,
  roles,
  onCreated,
  onClose,
}: {
  employeeId: string;
  roles: ResourceState<RoleListResponse>;
  onCreated: () => void;
  onClose: () => void;
}) {
  const [targetRoleId, setTargetRoleId] = useState('');
  const [directionDescription, setDirectionDescription] = useState('');
  const [priorityRank, setPriorityRank] = useState('');
  const [notes, setNotes] = useState('');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  // Mirrors the schema's own `career_aspiration_has_target` constraint
  // (migration 0020): at least a target role or a stated direction.
  const valid = targetRoleId !== '' || directionDescription.trim() !== '';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !valid) return;
    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/career-aspirations`, {
        targetRoleId: targetRoleId || undefined,
        directionDescription: directionDescription.trim() || undefined,
        priorityRank: priorityRank === '' ? undefined : Number(priorityRank),
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label="Add a career aspiration">
      <div className={formStyles.field}>
        <label htmlFor="new-aspiration-role" className={formStyles.label}>
          Target role <span className={formStyles.optional}>optional</span>
        </label>
        <RoleSelect id="new-aspiration-role" value={targetRoleId} onChange={setTargetRoleId} roles={roles} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="new-aspiration-direction" className={formStyles.label}>
          Direction <span className={formStyles.optional}>optional</span>
        </label>
        <textarea
          id="new-aspiration-direction"
          className={formStyles.textarea}
          rows={2}
          value={directionDescription}
          onChange={(event) => setDirectionDescription(event.target.value)}
          disabled={submitting}
          maxLength={2000}
          placeholder="A direction, if there is no single target role"
        />
        <p className={formStyles.help}>At least a target role or a stated direction is required.</p>
      </div>
      <div className={formStyles.field}>
        <label htmlFor="new-aspiration-priority" className={formStyles.label}>
          Priority <span className={formStyles.optional}>optional</span>
        </label>
        <input
          id="new-aspiration-priority"
          className={formStyles.rating}
          type="number"
          step={1}
          value={priorityRank}
          onChange={(event) => setPriorityRank(event.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor="new-aspiration-notes" className={formStyles.label}>
          Notes <span className={formStyles.optional}>optional</span>
        </label>
        <textarea
          id="new-aspiration-notes"
          className={formStyles.textarea}
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          disabled={submitting}
          maxLength={2000}
        />
      </div>
      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !valid}>
          {submitting ? 'Adding…' : 'Add aspiration'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

const STATUS_OPTIONS: CareerAspirationStatus[] = ['active', 'achieved', 'abandoned'];

function EditAspirationForm({
  employeeId,
  aspiration,
  roles,
  onSaved,
  onClose,
}: {
  employeeId: string;
  aspiration: CareerAspirationDetail;
  roles: ResourceState<RoleListResponse>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [targetRoleId, setTargetRoleId] = useState(aspiration.targetRoleId ?? '');
  const [directionDescription, setDirectionDescription] = useState(aspiration.directionDescription ?? '');
  const [priorityRank, setPriorityRank] = useState(aspiration.priorityRank === null ? '' : String(aspiration.priorityRank));
  const [status, setStatus] = useState<CareerAspirationStatus>(aspiration.status);
  const [notes, setNotes] = useState(aspiration.notes ?? '');
  const [state, setState] = useState<SubmitState>({ status: 'editing' });
  const submitting = state.status === 'submitting';
  const valid = targetRoleId !== '' || directionDescription.trim() !== '';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !valid) return;
    setState({ status: 'submitting' });
    try {
      await apiPatch(`/employees/${employeeId}/career-aspirations/${aspiration.id}`, {
        targetRoleId: targetRoleId || null,
        directionDescription: directionDescription.trim() || null,
        priorityRank: priorityRank === '' ? null : Number(priorityRank),
        status,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={formStyles.form} onSubmit={handleSubmit} aria-label="Edit this career aspiration">
      <div className={formStyles.field}>
        <label htmlFor={`edit-aspiration-role-${aspiration.id}`} className={formStyles.label}>
          Target role
        </label>
        <RoleSelect id={`edit-aspiration-role-${aspiration.id}`} value={targetRoleId} onChange={setTargetRoleId} roles={roles} />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-aspiration-direction-${aspiration.id}`} className={formStyles.label}>
          Direction
        </label>
        <textarea
          id={`edit-aspiration-direction-${aspiration.id}`}
          className={formStyles.textarea}
          rows={2}
          value={directionDescription}
          onChange={(event) => setDirectionDescription(event.target.value)}
          disabled={submitting}
          maxLength={2000}
        />
        <p className={formStyles.help}>At least a target role or a stated direction is required.</p>
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-aspiration-priority-${aspiration.id}`} className={formStyles.label}>
          Priority
        </label>
        <input
          id={`edit-aspiration-priority-${aspiration.id}`}
          className={formStyles.rating}
          type="number"
          step={1}
          value={priorityRank}
          onChange={(event) => setPriorityRank(event.target.value)}
          disabled={submitting}
        />
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-aspiration-status-${aspiration.id}`} className={formStyles.label}>
          Status
        </label>
        <select
          id={`edit-aspiration-status-${aspiration.id}`}
          className={styles.select}
          value={status}
          onChange={(event) => setStatus(event.target.value as CareerAspirationStatus)}
          disabled={submitting}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {STATUS_LABEL[option]}
            </option>
          ))}
        </select>
      </div>
      <div className={formStyles.field}>
        <label htmlFor={`edit-aspiration-notes-${aspiration.id}`} className={formStyles.label}>
          Notes <span className={formStyles.optional}>optional</span>
        </label>
        <textarea
          id={`edit-aspiration-notes-${aspiration.id}`}
          className={formStyles.textarea}
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          disabled={submitting}
          maxLength={2000}
        />
      </div>
      {state.status === 'failed' ? (
        <p className={formStyles.error} role="alert">
          {state.message}
        </p>
      ) : null}
      <div className={formStyles.actions}>
        <button type="submit" className={formStyles.primary} disabled={submitting || !valid}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className={formStyles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
